import { useCustom } from '@refinedev/core';
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import {
  CrownOutlined,
  FallOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import type {
  MonetizationAdminGrantedTierOut,
  MonetizationSnapshotOut,
} from '@/api/generated/schemas';
import { formatMoney } from '@/utils/money';
import { cleanFilters, type MonetizationStatsFilters } from './filters';

type Props = { filters: MonetizationStatsFilters };

type MoneyCardProps = {
  title: string;
  hint: string;
  byCurrency: Record<string, string> | undefined;
  loading: boolean;
};

const TileTitle = ({ title, hint }: { title: string; hint: string }) => (
  <>
    {title}{' '}
    <Tooltip title={hint}>
      <InfoCircleOutlined style={{ cursor: 'help' }} />
    </Tooltip>
  </>
);

const sortedByAmount = (byCurrency: Record<string, string> | undefined) =>
  Object.entries(byCurrency ?? {})
    .map(([currency, amount]) => ({ currency, amount: Number(amount) }))
    .sort((a, b) => b.amount - a.amount);

// An empty cost map with accounts means the tier has no paying users to
// price it with — that is "no price", not zero.
const formatAllCurrencies = (
  byCurrency: Record<string, string> | undefined,
  accounts: number
) => {
  const entries = sortedByAmount(byCurrency);
  if (entries.length > 0) {
    return entries.map((e) => formatMoney(e.amount, e.currency)).join(' · ');
  }
  return accounts > 0 ? 'brak ceny' : formatMoney(0);
};

const MONTHLY_COST_HINT =
  'Ile co miesiąc płaciliby ci użytkownicy, gdyby płacili jak średni płacący użytkownik w tym samym tierze. „Brak ceny” znaczy, że w tierze nie ma płacących użytkowników.';
const COST_UNTIL_EXPIRY_HINT =
  'Ile łącznie, od dziś do końca przyznanego dostępu, zapłaciliby ci użytkownicy, gdyby płacili jak średni płacący użytkownik w tym samym tierze. „Brak ceny” znaczy, że w tierze nie ma płacących użytkowników.';

const tierColumns: TableColumnsType<MonetizationAdminGrantedTierOut> = [
  {
    title: 'Tier',
    dataIndex: 'tier',
    render: (tier: string) => tier.replace('tier_', 'Tier '),
  },
  { title: 'Konta', dataIndex: 'accounts', align: 'right' },
  {
    title: <TileTitle title="Koszt miesięczny" hint={MONTHLY_COST_HINT} />,
    render: (_, row) =>
      formatAllCurrencies(row.monthly_cost_by_currency, row.accounts),
  },
  {
    title: (
      <TileTitle title="Koszt do wygaśnięcia" hint={COST_UNTIL_EXPIRY_HINT} />
    ),
    render: (_, row) =>
      formatAllCurrencies(row.cost_until_expiry_by_currency, row.accounts),
  },
];

const MoneyCard = ({ title, hint, byCurrency, loading }: MoneyCardProps) => {
  const [primary, ...secondary] = sortedByAmount(byCurrency);

  return (
    <Card loading={loading}>
      <Statistic
        title={<TileTitle title={title} hint={hint} />}
        value={primary?.amount ?? 0}
        formatter={(v) => formatMoney(v, primary?.currency ?? 'PLN')}
      />
      {secondary.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {secondary.map((entry) => (
            <Typography.Text
              key={entry.currency}
              type="secondary"
              style={{ fontSize: 12, display: 'block' }}
            >
              {formatMoney(entry.amount, entry.currency)}
            </Typography.Text>
          ))}
        </div>
      )}
    </Card>
  );
};

export const MonetizationKpiCards = ({ filters }: Props) => {
  const { query } = useCustom<MonetizationSnapshotOut>({
    url: 'monetization/subscriptions/stats/snapshot',
    method: 'get',
    config: { query: cleanFilters(filters) },
  });

  const { query: tierQuery } = useCustom<MonetizationAdminGrantedTierOut[]>({
    url: 'monetization/entitlements/stats/admin_granted_by_tier',
    method: 'get',
    config: { query: cleanFilters(filters) },
  });

  const stats = query.data?.data;
  const adminGrantedAccounts = stats?.admin_granted_accounts ?? 0;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={8}>
        <Card loading={query.isLoading}>
          <Statistic
            title={
              <TileTitle
                title="Aktywni subskrybenci"
                hint="Konta, które mają teraz aktywną płatną subskrypcję."
              />
            }
            value={stats?.active_subscribers ?? 0}
            prefix={<CrownOutlined />}
            valueStyle={{ color: '#1677ff' }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={8}>
        <Card loading={query.isLoading}>
          <Statistic
            title={
              <TileTitle
                title="Nowi (ten miesiąc)"
                hint="Konta, które w tym miesiącu kupiły subskrypcję po raz pierwszy. Odnowienia się nie liczą."
              />
            }
            value={stats?.new_subscribers_this_month ?? 0}
            prefix={<RiseOutlined />}
            valueStyle={{ color: '#3f8600' }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={8}>
        <Card loading={query.isLoading}>
          <Statistic
            title={
              <TileTitle
                title="Rezygnacje (ten miesiąc)"
                hint="Konta, którym w tym miesiącu skończyła się subskrypcja i jej nie odnowią."
              />
            }
            value={stats?.churned_this_month ?? 0}
            prefix={<FallOutlined />}
            valueStyle={{
              color:
                (stats?.churned_this_month ?? 0) > 0 ? '#cf1322' : undefined,
            }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12}>
        <MoneyCard
          title="MRR"
          hint="Miesięczny przychód z aktywnych subskrypcji."
          byCurrency={stats?.mrr_by_currency}
          loading={query.isLoading}
        />
      </Col>

      <Col xs={24} sm={12}>
        <MoneyCard
          title="ARR"
          hint="Roczny przychód z aktywnych subskrypcji (MRR × 12)."
          byCurrency={stats?.arr_by_currency}
          loading={query.isLoading}
        />
      </Col>

      <Col xs={24}>
        <Card loading={query.isLoading}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Statistic
                title={
                  <TileTitle
                    title="Przyznane przez admina"
                    hint="Konta z aktywnym dostępem nadanym ręcznie przez admina, a pod spodem to, ile te konta płaciłyby za ten dostęp."
                  />
                }
                value={adminGrantedAccounts}
                prefix={<GiftOutlined />}
              />
              <div style={{ marginTop: 4 }}>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block' }}
                >
                  <TileTitle
                    title="Koszt miesięczny"
                    hint={MONTHLY_COST_HINT}
                  />
                  :{' '}
                  {formatAllCurrencies(
                    stats?.admin_granted_monthly_cost_by_currency,
                    adminGrantedAccounts
                  )}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, display: 'block' }}
                >
                  <TileTitle
                    title="Koszt do wygaśnięcia"
                    hint={COST_UNTIL_EXPIRY_HINT}
                  />
                  :{' '}
                  {formatAllCurrencies(
                    stats?.admin_granted_cost_until_expiry_by_currency,
                    adminGrantedAccounts
                  )}
                </Typography.Text>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <Table
                size="small"
                rowKey="tier"
                pagination={false}
                loading={tierQuery.isLoading}
                columns={tierColumns}
                dataSource={tierQuery.data?.data ?? []}
                scroll={{ x: 'max-content' }}
              />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
};
